import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import { DeleteIconList } from "@platformx/shared/static-assets";
import { ShowToastError, TitleSubTitle } from "@platformx/utilities";
import { useCallback, useEffect, useState } from "react";
import { Control, FieldErrors, UseFormClearErrors, UseFormRegister } from "react-hook-form";
import {
  getRepeaterValue,
  RepeaterCoordinate,
  setRepeaterValue,
} from "../../../utils/repeaterPath";
import builder from "../../builder";
import { disabledBlockSx } from "../disabledStyles";

interface RepeaterField {
  name: string;
  validations?: { type: string; value: string }[];
  options?: any[];
  defaultLength?: number;
  // Present when this repeater is itself nested inside another repeater: the
  // ancestor path down to (but not including) this repeater, and this
  // repeater's own real key (since `name` above may be the synthetic,
  // mangled RHF name assigned by the parent CustomRepeater in that case).
  repeaterPath?: RepeaterCoordinate[];
  childName?: string;
  [key: string]: any;
}

interface RepeaterProps {
  field: RepeaterField;
  control: Control<any>;
  errors: FieldErrors<any>;
  register: UseFormRegister<any>;
  clearErrors: UseFormClearErrors<any>;
  state: any;
  setState: any;
  isEdit?: boolean;
  title?: string;
  isDisabled?: boolean;
}

const DEFAULT_MAXIMUM_ITEMS = 10;

// A radio shows its default_value even when nothing is stored, so a new row has to store it too -
// otherwise the row displays one choice while the saved value is empty.
const initialChildValue = (child: any) =>
  child?.type === "radio" && child?.default_value ? child.default_value : "";

const CustomRepeater = ({
  field,
  control,
  errors,
  register,
  clearErrors,
  state,
  setState,
  isEdit,
  title,
  isDisabled = false,
}: RepeaterProps) => {
  const [localItems, setLocalItems] = useState<any[]>([]);
  // When this repeater is nested inside another one, `field.name` is the
  // synthetic RHF name the parent assigned (not a real key) — the real key and
  // the ancestor path down to it live in `childName`/`repeaterPath` instead.
  // For a top-level repeater neither is set, so `ownKey` falls back to `name`.
  const ownKey = field.childName ?? field.name;
  const ownRepeaterPath = field.repeaterPath;
  const repeaterValidation = field.validations?.find((v: any) => v.type === "repeater");
  const maxItems = repeaterValidation
    ? parseInt(repeaterValidation.value, 10)
    : DEFAULT_MAXIMUM_ITEMS;
  const defaultLength = field.defaultLength || 0;
  const hasFixedLength = defaultLength > 0;
  const [usedIds, setUsedIds] = useState<Set<string>>(new Set());

  const generateUniqueId = useCallback(() => {
    let newId: string;
    do {
      const randomNumber = Math.floor(Math.random() * 90000) + 10000;
      newId = randomNumber.toString();
    } while (usedIds.has(newId));
    setUsedIds((prev) => new Set(prev).add(newId));
    return newId;
  }, [usedIds]);

  // `id` is a client-only tracking key (React list key + unique field-naming
  // suffix) — it must never be written into `state[field.name]`, since that
  // object is exactly what gets submitted to the backend, and the schema
  // doesn't declare an "id" field on repeater rows. The save/update endpoint
  // tolerates the extra key, but publish validates the stored document
  // strictly against the schema and rejects it as an unknown field.
  const stripId = (item: { [key: string]: any }) => {
    const { id: _id, ...rest } = item;
    return rest;
  };

  const addNewItem = () => {
    if (localItems?.length >= maxItems) {
      ShowToastError(`Maximum of ${maxItems} items allowed.`);
      return;
    }
    // Create a default object based on the options schema
    const newObject: { [key: string]: any } = {};
    field.options?.forEach((child: any) => {
      newObject[child.name] = initialChildValue(child);
    });
    newObject["id"] = `${field.name}_${generateUniqueId()}`; // Local-only, for React keys/field naming
    setLocalItems([...localItems, newObject]);
    // Update external state to keep it synchronized
    if (state && typeof setState === "function") {
      const currentArray = getRepeaterValue(state, ownRepeaterPath, ownKey) || [];
      setState(
        setRepeaterValue(state, ownRepeaterPath, ownKey, [...currentArray, stripId(newObject)]),
      );
    }
  };

  const removeItem = (id: string) => {
    // Prevent removing if fixed length or only one item
    if (hasFixedLength) {
      ShowToastError("Cannot remove items from a fixed length repeater.");
      return;
    }

    if (localItems?.length <= 1) {
      ShowToastError("At least one item is required.");
      return;
    }
    const removeIndex = localItems.findIndex((x) => x.id === id);
    setLocalItems(localItems.filter((x) => x.id !== id));
    // Update external state to keep it synchronized — state items don't carry
    // `id`, so remove by the matching position instead.
    if (state && typeof setState === "function" && removeIndex >= 0) {
      const currentArray = getRepeaterValue(state, ownRepeaterPath, ownKey) || [];
      setState(
        setRepeaterValue(
          state,
          ownRepeaterPath,
          ownKey,
          currentArray.filter((_y, index) => index !== removeIndex),
        ),
      );
    }
  };

  // Initialize default items
  useEffect(() => {
    const existingArray = getRepeaterValue(state, ownRepeaterPath, ownKey);
    if (existingArray?.length > 0) {
      // Existing rows loaded from the server never carry `id` — assign a
      // local-only one for React keys/field naming without writing it back.
      setLocalItems(
        existingArray.map((item) => ({
          ...item,
          id: item.id || `${field.name}_${generateUniqueId()}`,
        })),
      );
    } else if (localItems?.length === 0) {
      const itemsToAdd = hasFixedLength ? defaultLength : 1;
      const defaultObjects: { [key: string]: any }[] = [];

      for (let i = 0; i < itemsToAdd; i++) {
        const defaultObject: { [key: string]: any } = {};
        field?.options?.forEach((child: any) => {
          defaultObject[child.name] = initialChildValue(child);
        });
        defaultObject["id"] = `${field.name}_${generateUniqueId()}`; // Local-only
        defaultObjects.push(defaultObject);
      }
      setLocalItems([...defaultObjects]);
      if (defaultObjects.length > 0) {
        if (state && typeof setState === "function") {
          setState(setRepeaterValue(state, ownRepeaterPath, ownKey, defaultObjects.map(stripId)));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.options, hasFixedLength, defaultLength, field.name, ownKey]);

  // The effect above only runs on mount. When saved data arrives afterwards (the form can mount
  // before its fetch resolves), the state holds more/fewer rows than are rendered - the extra
  // rows then stay invisible but are still submitted. Re-sync the rendered rows whenever the
  // state's row count stops matching. Add/remove update both together, so they never trip this.
  const stateRowCount = getRepeaterValue(state, ownRepeaterPath, ownKey)?.length ?? 0;
  useEffect(() => {
    if (stateRowCount > 0 && stateRowCount !== localItems.length) {
      const existingArray = getRepeaterValue(state, ownRepeaterPath, ownKey) || [];
      setLocalItems(
        existingArray.map((item, index) => ({
          ...item,
          id: localItems[index]?.id || item.id || `${field.name}_${generateUniqueId()}`,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRowCount]);

  return (
    <Box
      sx={{
        border: "1px solid #D9DBE9",
        borderRadius: "8px",
        padding: "24px",
        backgroundColor: "#FAFAFC",
        width: "100%",
        ...disabledBlockSx(isDisabled),
      }}
      id={`${field?.name}_id`}>
      {localItems?.map((item, index) => (
        <Box
          key={item.id}
          display='flex'
          flexDirection='column'
          mb={3}
          sx={{
            border: "1px solid #E4E6F3",
            borderRadius: 3,
            padding: "20px 24px",
            position: "relative",
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px 0 rgba(20, 20, 43, 0.04)",
            width: "100%",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}>
            <Typography variant='h6medium' sx={{ color: "#01B2AD" }}>
              {field.title} {index + 1}
            </Typography>
            {!hasFixedLength && localItems?.length > 1 && (
              <IconButton aria-label='Delete row' onClick={() => removeItem(item.id)} size='small'>
                <img src={DeleteIconList} alt='delete' />
              </IconButton>
            )}
          </Box>
          <Divider sx={{ mb: 3 }} />
          {field.options?.map((childField: any) => {
            // The RHF-registered path for this leaf. react-hook-form parses this exact
            // dot/bracket string into its nested errors object (errors.<field.name>[index].
            // <childField.name>), so the trailing segment MUST be the plain childField.name -
            // errorDestructure/CustomRepeater's own narrowing (errors?.[ownKey]?.[index]) both
            // key off childField.name, and the array index already makes this unique per row,
            // so no extra per-item suffix is needed (or safe to add without breaking that match).
            const nestedFieldName = `${field.name}[${index}].${childField.name}`;
            // Append this repeater's own coordinate to the ancestor path so a
            // repeater nested inside this one still resolves correctly.
            const childRepeaterPath: RepeaterCoordinate[] = [
              ...(ownRepeaterPath || []),
              { name: ownKey, index },
            ];
            const fieldForBuilder = {
              ...childField,
              id: `${childField.name}_${item.id}`, // Ensure unique ID for each child field
              name: nestedFieldName,
              repeaterPath: childRepeaterPath,
              childName: childField.name,
            };

            // `errors` is narrowed by exactly one repeater level per CustomRepeater
            // render, using this repeater's own real key — matches the state path.
            const nestedFieldErrors = errors?.[ownKey]?.[index];

            return (
              <Box
                key={`${item.id}_${childField.id || childField.name}`}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 3 },
                  mb: 3,
                }}>
                <Box sx={{ width: { xs: "100%", sm: "30%" }, flexShrink: 0 }}>
                  <TitleSubTitle
                    titleVariant='h6medium'
                    subTitleVariant='p4regular'
                    title={
                      childField?.validations?.some((v: any) => v.type === "required")
                        ? `${childField.title}*`
                        : childField.title
                    }
                    subTitle={childField?.description}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", sm: "70%" } }}>
                  {builder(
                    fieldForBuilder,
                    state,
                    nestedFieldErrors,
                    register,
                    clearErrors,
                    setState,
                    field.options,
                    isEdit,
                    title,
                    control,
                    null,
                    true, //this is for refer inside repeater field
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
      {!hasFixedLength && (
        <Button
          variant='primaryButton1'
          startIcon={<AddIcon />}
          onClick={addNewItem}
          disabled={localItems?.length >= maxItems}
          sx={{ mt: 1, padding: "9px 16px", fontSize: "14px", height: "38px", minWidth: "auto" }}>
          Add Another Group
        </Button>
      )}
      {!hasFixedLength && (
        <Typography variant='caption' color='text.secondary' sx={{ display: "block", mt: 1 }}>
          {`Maximum ${maxItems} ${maxItems > 1 ? "items" : "item"} allowed`}
        </Typography>
      )}
    </Box>
  );
};

export default CustomRepeater;
